/** QuickRouter — Rendu SVG statique du schéma : image que voit l'assistant (rasterisée par le
 *  serveur) et export SVG / PDF. Même dessin que l'éditeur (shared/sch-draw.js) ; textes en
 *  traits vectoriels, aucune police requise. */
import { schematicDrawing } from './sch-draw.js';
import { connectivity } from './schematic.js';
import { strokeText } from './export/font.js';

export const SCH_COLORS = {
  paper: '#f7f5ee', grid: '#d6d2c4', body: '#7a1f1f', bodyFill: '#fffbe3', pin: '#7a1f1f',
  pinnum: '#7a5a5a', pinname: '#3a2a2a', ref: '#1d3f7a', value: '#1d3f7a', wire: '#0b4f94',
  label: '#7a4400', labelFill: '#fff3d6', power: '#7a1f1f', junction: '#0b4f94', hi: '#ff7a00', erc: '#e0007a', note: '#444',
};

const n = (v) => Math.round(v * 1000) / 1000;
const ALIGN = { start: 'left', middle: 'center', end: 'right' };

function color(cls) {
  return SCH_COLORS[cls] ?? SCH_COLORS.body;
}

/** Une primitive → fragment SVG (repère Y vers le haut : le groupe parent est retourné). */
export function primSvg(p, { hi = false } = {}) {
  const stroke = hi ? SCH_COLORS.hi : color(p.cls);
  const w = p.cls === 'wire' ? 0.3 : p.cls === 'pin' ? 0.22 : 0.25;
  if (p.t === 'line') return `<polyline points="${p.pts.map((q) => `${n(q[0])},${n(q[1])}`).join(' ')}" fill="none" stroke="${stroke}" stroke-width="${w}" stroke-linecap="round" stroke-linejoin="round"/>`;
  if (p.t === 'poly') {
    const fill = p.fill === 'body' ? SCH_COLORS.bodyFill : p.fill === 'label' ? SCH_COLORS.labelFill : p.fill === 'ink' ? stroke : 'none';
    return `<polygon points="${p.pts.map((q) => `${n(q[0])},${n(q[1])}`).join(' ')}" fill="${fill}" stroke="${stroke}" stroke-width="${w}" stroke-linejoin="round"/>`;
  }
  if (p.t === 'circle') return `<circle cx="${n(p.c[0])}" cy="${n(p.c[1])}" r="${n(p.r)}" fill="${p.fill ? stroke : 'none'}" stroke="${stroke}" stroke-width="${w}"/>`;
  if (p.t === 'text') {
    const { lines } = strokeText(p.text, { pos: p.pos, size: p.size * 0.78, rot: p.rot || 0, align: ALIGN[p.anchor] ?? 'center' });
    if (!lines.length) return '';
    const d = lines.map((l) => `M${l.map(([x, y]) => `${n(x)} ${n(y)}`).join('L')}`).join('');
    return `<path d="${d}" fill="none" stroke="${hi ? SCH_COLORS.hi : color(p.cls)}" stroke-width="${n(p.size * 0.11)}" stroke-linecap="round" stroke-linejoin="round"/>`;
  }
  return '';
}

/**
 * options : { scale (px/mm, 6), highlight (noms de nets), erc (résultat de runErc), margin (mm) }
 */
export function renderSchematicSvg(project, lookup, opts = {}) {
  const scale = opts.scale ?? 6;
  const dr = schematicDrawing(project, lookup);
  const m = opts.margin ?? 8;
  const [x0, y0, x1, y1] = dr.box;
  const W = x1 - x0 + 2 * m, H = y1 - y0 + 2 * m;
  const hiNames = new Set(opts.highlight ?? []);
  // Mise en évidence : groupes portant un des noms demandés (ou dont une broche est sur ce net).
  const hiWires = new Set(), hiPins = new Set();
  if (hiNames.size) {
    const netOfPin = new Map(project.nets.flatMap((nt) => nt.pins.map((k) => [k, nt.name])));
    const { wires } = connectivity(project, lookup);
    for (const [id, g] of wires) if ([...g.pins].some((k) => hiNames.has(netOfPin.get(k))) || [...g.names].some((x) => hiNames.has(x))) hiWires.add(id);
    for (const [k, name] of netOfPin) if (hiNames.has(name)) hiPins.add(k);
  }
  const out = [];
  out.push(`<svg xmlns="http://www.w3.org/2000/svg" width="${Math.round(W * scale)}" height="${Math.round(H * scale)}" viewBox="0 0 ${n(W)} ${n(H)}">`);
  out.push(`<rect width="100%" height="100%" fill="${SCH_COLORS.paper}"/>`);
  out.push(`<g transform="translate(${n(m - x0)} ${n(H - m + y0)}) scale(1 -1)">`);
  for (const w of dr.wires) out.push(primSvg({ t: 'line', pts: w.pts, cls: 'wire' }, { hi: hiWires.has(w.id) }));
  for (const s of dr.symbols) for (const p of s.prims) out.push(primSvg(p));
  for (const p of dr.pins) if (hiPins.has(p.key)) out.push(`<circle cx="${n(p.pos[0])}" cy="${n(p.pos[1])}" r="0.6" fill="${SCH_COLORS.hi}"/>`);
  for (const l of dr.labels) for (const p of l.prims) out.push(primSvg(p, { hi: hiNames.has(l.label.name) }));
  for (const d of dr.dots) out.push(`<circle cx="${n(d[0])}" cy="${n(d[1])}" r="0.5" fill="${SCH_COLORS.junction}"/>`);
  for (const t of dr.notes) out.push(primSvg({ t: 'text', text: t.text, pos: t.pos, size: t.size, anchor: 'start', cls: 'note' }));
  if (opts.erc) {
    for (const e of [...opts.erc.errors, ...opts.erc.warnings]) {
      if (e.at) out.push(`<circle cx="${n(e.at[0])}" cy="${n(e.at[1])}" r="1.4" fill="none" stroke="${SCH_COLORS.erc}" stroke-width="0.3"/>`);
    }
  }
  out.push('</g></svg>');
  return out.join('');
}
